
import React, { useState, useEffect } from 'react';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, ArrowRightLeft, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { RuleImprovementSuggestion, getPendingRuleSuggestions, implementRuleImprovement } from '@/services/ai-rule-improvement-service';
import { Skeleton } from '@/components/ui/skeleton';
import { useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart } from '@/components/ui/bar-chart';

export const RuleImprovementSuggestions = () => {
  const [suggestions, setSuggestions] = useState<RuleImprovementSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSuggestion, setSelectedSuggestion] = useState<RuleImprovementSuggestion | null>(null);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [implementingRule, setImplementingRule] = useState(false);
  
  // Load suggestions
  useEffect(() => {
    const loadSuggestions = async () => {
      setIsLoading(true);
      try {
        const data = await getPendingRuleSuggestions();
        setSuggestions(data);
      } catch (error) {
        console.error('Error loading rule suggestions:', error);
        toast.error('Failed to load rule improvement suggestions');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSuggestions();
    
    // Refresh suggestions every 5 minutes
    const interval = setInterval(loadSuggestions, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Handle implementing or rejecting a suggested improvement
  const handleImplementSuggestion = async (suggestionId: string, approved: boolean) => {
    setImplementingRule(true);
    try {
      const result = await implementRuleImprovement(suggestionId, approved);
      
      if (result.success) {
        toast.success(approved ? 'Rule updated successfully' : 'Suggestion rejected');
        
        // Update local state to remove the implemented/rejected suggestion
        setSuggestions(suggestions.filter(s => s !== selectedSuggestion));
        setShowSuggestionDialog(false);
      } else {
        toast.error(approved ? 'Failed to update rule' : 'Failed to reject suggestion');
      }
    } catch (error) {
      console.error('Error implementing rule suggestion:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setImplementingRule(false);
    }
  };
  
  // Prepare chart data
  const chartData = useMemo(() => {
    const data = suggestions.map(s => ({
      name: s.pattern.length > 20 ? s.pattern.substring(0, 20) + '...' : s.pattern,
      value: s.negative_ratio
    })).slice(0, 5);
    
    return data;
  }, [suggestions]);
  
  // Get confidence level display
  const getConfidenceDisplay = (score: number) => {
    if (score >= 0.8) return { label: 'High', color: 'bg-green-500' };
    if (score >= 0.5) return { label: 'Medium', color: 'bg-yellow-500' };
    return { label: 'Low', color: 'bg-red-500' };
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rule Improvement Suggestions</CardTitle>
          <CardDescription>AI-generated suggestions to improve underperforming rules</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-[200px] w-full" />
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (suggestions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rule Improvement Suggestions</CardTitle>
          <CardDescription>AI-generated suggestions to improve underperforming rules</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <CheckCircle className="h-16 w-16 text-muted-foreground mb-4" />
          <p className="text-center text-muted-foreground">No rule improvement suggestions at this time.</p>
          <p className="text-center text-muted-foreground text-sm mt-1">
            Suggestions will appear here when rules receive negative feedback.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Rule Improvement Suggestions</CardTitle>
          <CardDescription>AI-generated suggestions to improve underperforming rules</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="list">
            <TabsList className="mb-4">
              <TabsTrigger value="list">List View</TabsTrigger>
              <TabsTrigger value="chart">Chart View</TabsTrigger>
            </TabsList>
            
            <TabsContent value="list">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pattern</TableHead>
                      <TableHead>Negative Feedback</TableHead>
                      <TableHead className="w-[150px]">Confidence</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suggestions.slice(0, 5).map((suggestion, index) => {
                      const confidence = getConfidenceDisplay(suggestion.confidence_score);
                      
                      return (
                        <TableRow key={index}>
                          <TableCell className="font-mono text-xs truncate max-w-[200px]">
                            {suggestion.pattern}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <span className="mr-2">{suggestion.negative_ratio.toFixed(1)}%</span>
                              {suggestion.negative_ratio > 70 && (
                                <AlertTriangle className="h-4 w-4 text-destructive" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={`${confidence.color} bg-opacity-15 border-0 text-xs`}
                            >
                              {confidence.label} confidence
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => {
                                setSelectedSuggestion(suggestion);
                                setShowSuggestionDialog(true);
                              }}
                            >
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              
              {suggestions.length > 5 && (
                <div className="mt-4 text-center">
                  <Button variant="outline" size="sm">
                    View all {suggestions.length} suggestions
                  </Button>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="chart">
              <div className="h-[300px]">
                <BarChart
                  data={chartData}
                  index="name"
                  categories={['value']}
                  yAxisWidth={40}
                  className="h-full"
                  valueFormatter={(value) => `${value.toFixed(1)}%`}
                  customTooltip={(props) => (
                    <div className="bg-background border p-2 rounded-md shadow-md">
                      <div className="font-medium">{props.payload?.[0]?.payload.name}</div>
                      <div>Negative Feedback: {props.payload?.[0]?.value}%</div>
                    </div>
                  )}
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Suggestion Review Dialog */}
      {selectedSuggestion && (
        <Dialog open={showSuggestionDialog} onOpenChange={setShowSuggestionDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Rule Improvement Suggestion</DialogTitle>
              <DialogDescription>Review and implement the suggested change</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div>
                <h4 className="text-sm font-medium mb-1">Pattern (Regex)</h4>
                <pre className="bg-muted p-2 rounded-md text-xs overflow-x-auto">
                  {selectedSuggestion.pattern}
                </pre>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Current Response</h4>
                  <div className="h-[300px] overflow-y-auto border rounded-md p-3 text-sm">
                    <ReactMarkdown>
                      {selectedSuggestion.current_response}
                    </ReactMarkdown>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Suggested Improvement</h4>
                  <div className="h-[300px] overflow-y-auto border rounded-md p-3 text-sm bg-primary/5">
                    <ReactMarkdown>
                      {selectedSuggestion.suggested_improvement}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-1">Stats</h4>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center">
                    <ArrowRightLeft className="h-4 w-4 mr-1" />
                    <span className="text-sm">Used {selectedSuggestion.total_usage} times</span>
                  </div>
                  <div className="flex items-center">
                    <ThumbsDown className="h-4 w-4 mr-1 text-destructive" />
                    <span className="text-sm">
                      {selectedSuggestion.negative_feedback_count} negative ({selectedSuggestion.negative_ratio.toFixed(1)}%)
                    </span>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`${getConfidenceDisplay(selectedSuggestion.confidence_score).color} bg-opacity-15 border-0`}
                  >
                    {getConfidenceDisplay(selectedSuggestion.confidence_score).label} confidence score
                  </Badge>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <div className="flex justify-between w-full">
                <Button 
                  variant="destructive" 
                  onClick={() => handleImplementSuggestion(selectedSuggestion.rule_id.toString(), false)}
                  disabled={implementingRule}
                >
                  Reject Suggestion
                </Button>
                
                <Button 
                  onClick={() => handleImplementSuggestion(selectedSuggestion.rule_id.toString(), true)}
                  disabled={implementingRule}
                >
                  Implement Improvement
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
